// app/frontend/components/EventForm/sections/EventInfoSection.tsx
import { useState, useEffect } from 'react';
import { Accordion, Group, Text, MultiSelect, TextInput, Tooltip, ActionIcon } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconCalendarPlus, IconInfoCircle } from '@tabler/icons-react';
import { EventInfoSectionProps, User, SlugStatus } from '../types';
import { generateSlug } from '../utils/formHelpers';

const EventInfoSection: React.FC<EventInfoSectionProps> = ({ form, slug, setSlug, organizers, user }) => {
  const [tooltipOpenedOrganizer, setTooltipOpenedOrganizer] = useState(false);
  const [debouncedSlug] = useDebouncedValue(slug, 500);
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');

  // Filter organizers based on user permissions
  const filteredOrganizers = user?.roles?.includes('SuperAdmin')
    ? organizers
    : organizers.filter((org) => user?.organizer_ids?.includes(org.id));

  // Sort organizers alphabetically
  const sortedOrganizers = [...filteredOrganizers].sort((a, b) => a.name.localeCompare(b.name));

  // Handle event name change and auto-generate slug
  const handleEventNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEventName = e.target.value;
    form.setFieldValue('name', newEventName);

    // Generate parameterized slug
    const parameterizedSlug = generateSlug(newEventName);
    setSlug(parameterizedSlug);
  };

  // Check slug availability when debounced value changes
  useEffect(() => {
    async function checkSlugAvailability() {
      if (!debouncedSlug || debouncedSlug.length < 3) {
        setSlugStatus('idle');
        return;
      }

      setSlugStatus('checking');

      try {
        const url = `/events/check_slug?slug=${encodeURIComponent(debouncedSlug)}`;
        const response = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await response.json();
          setSlugStatus(data.valid ? 'valid' : 'invalid');
        } else {
          setSlugStatus('idle');
        }
      } catch (error) {
        console.error('Error checking slug availability:', error);
        setSlugStatus('idle');
      }
    }

    checkSlugAvailability();
  }, [debouncedSlug]);

  return (
    <Accordion multiple className="form-accordion">
      <Accordion.Item value="event-info">
        <Accordion.Control>
          <Group gap="xs">
            <IconCalendarPlus size={28} />
            <Text style={{ fontSize: '1.25rem', fontWeight: 'bold' }}>Event Host & Name</Text>
          </Group>
        </Accordion.Control>
        <Accordion.Panel>
          {/* Event Host/Organizer Selection */}
          <MultiSelect
            label={
              <Group gap={4} align="center">
                <Text className="mb-2">Event Host</Text>
                <Tooltip
                  label="Organizers you're linked to will appear here. If the list is empty, you can continue filling out the form and submit the event. Afterwards, create an organizer profile and return later to add the organizer to this event."
                  multiline
                  w={220}
                  withArrow
                  position="bottom"
                  color="gray.9"
                  transitionProps={{ duration: 200 }}
                  opened={tooltipOpenedOrganizer}
                >
                  <ActionIcon
                    variant="transparent"
                    size="sm"
                    className="mb-2"
                    onClick={() => setTooltipOpenedOrganizer((prev) => !prev)}
                    onMouseEnter={() => setTooltipOpenedOrganizer(true)}
                    onMouseLeave={() => setTooltipOpenedOrganizer(false)}
                    style={{ cursor: 'pointer' }}
                  >
                    <IconInfoCircle size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>
            }
            placeholder={
              form.values.organizer_ids && form.values.organizer_ids.length > 0 ? '' : 'Select event organizers'
            }
            name="organizer_ids"
            hidePickedOptions
            comboboxProps={{ shadow: 'xl' }}
            data={sortedOrganizers.map((org) => ({
              value: org.id.toString(),
              label: org.name,
            }))}
            value={(form.values.organizer_ids || []).map((id: number) => id.toString())}
            onChange={(values) => {
              const numericValues = Array.isArray(values) ? values.map((v) => Number(v)) : [];
              form.setFieldValue('organizer_ids', numericValues);
            }}
            searchable
            clearable
            mt="md"
            styles={{
              label: { color: '#FFF7ED', fontSize: '16px' },
              input: { fontSize: '16px' },
              dropdown: { fontSize: '16px' },
            }}
          />

          {/* Event Name */}
          <TextInput
            label="Event Name"
            name="name"
            placeholder="Enter the name"
            {...form.getInputProps('name')}
            onChange={handleEventNameChange}
            mt="md"
            required
            styles={{ label: { color: '#FFF7ED' } }}
          />

          {/* Slug */}
          <TextInput
            label="Slug"
            name="slug"
            value={slug}
            placeholder="your-event-slug"
            {...form.getInputProps('slug', {
              onChange: (e: React.ChangeEvent<HTMLInputElement>) => setSlug(e.target.value),
            })}
            mt="md"
            required
            styles={{ label: { color: '#FFF7ED' } }}
          />

          {/* Slug Status Messages */}
          {slugStatus === 'checking' && (
            <Text size="sm" c="gray">
              Checking availability...
            </Text>
          )}
          {slugStatus === 'valid' && (
            <Text size="sm" c="green">
              Slug is available.
            </Text>
          )}
          {slugStatus === 'invalid' && (
            <Text size="sm" c="red">
              Slug is already taken.
            </Text>
          )}
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
};

export default EventInfoSection;
